/**
 * ExplicitConsentMethod Integration Layer
 * Rule: SDK -> Store bridge with UI feedback
 */
import i18next from 'i18next';
import { getIntegrationErrorMessage } from '@/lib/integration-error-message.js';
import { toast } from 'sonner';
import { useExplicitConsentMethodStore } from '@/store/explicit-consent-method.store.js';
import { useTenantStore } from '@/store/tenant.store';
import { fetchDropdownOptionsFromReference } from '@/lib/reference-dropdown.helper.js';

export class ExplicitConsentMethodIntegration {
    constructor(sdkService) {
        this.service = sdkService;
    }

    /**
     * Fetch explicit consent methods and update store
     */
    async fetchExplicitConsentMethods(page = 1, pageSize = 10, search = '', sortBy = '', sortOrder = 'asc', columnFilters = {}) {
        const store = useExplicitConsentMethodStore.getState();
        const tenantId = useTenantStore.getState().selectedTenantId;

        store.setLoading(true);
        store.clearError();

        if (!tenantId) {
            store.setLoading(false);
            return { success: false, error: getIntegrationErrorMessage({ message: 'Tenant context required' }) };
        }

        try {
            const response = await this.service.getAll({
                page,
                pageSize,
                search,
                sortBy,
                sortOrder,
                tenantId,
                ...Object.entries(columnFilters).reduce((acc, [key, value]) => {
                    if (value && value.length > 0) {
                        acc[`filter_${key}`] = value;
                    }
                    return acc;
                }, {})
            });

            const { data, pagination, uniqueValues } = response;
            store.setMethods(data || []);

            if (pagination) {
                store.setPaginationData({
                    currentPage: pagination.page,
                    totalPages: pagination.totalPages,
                    totalItems: pagination.total,
                    itemsPerPage: pagination.pageSize,
                    hasNextPage: pagination.page < pagination.totalPages,
                    hasPreviousPage: pagination.page > 1,
                });
            } else {
                store.setPaginationData({
                    currentPage: page,
                    totalPages: Math.ceil((data?.length || 0) / pageSize) || 1,
                    totalItems: data?.length || 0,
                    itemsPerPage: pageSize,
                    hasNextPage: false,
                    hasPreviousPage: false,
                });
            }

            if (uniqueValues) {
                store.setUniqueValues(uniqueValues);
            }

            return { success: true };
        } catch (error) {
            const errorMessage = this._getErrorMessage(error);
            store.setError(errorMessage);
            toast.error(errorMessage);
            return { success: false, error: errorMessage };
        } finally {
            store.setLoading(false);
        }
    }

    /**
     * Create explicit consent method
     */
    async createExplicitConsentMethod(data) {
        const store = useExplicitConsentMethodStore.getState();
        const tenantId = useTenantStore.getState().selectedTenantId;

        store.setLoading(true);

        if (!tenantId) {
            store.setLoading(false);
            return { success: false, error: getIntegrationErrorMessage({ message: 'Tenant context required' }) };
        }

        try {
            const response = await this.service.create({ ...data, tenantId });
            toast.success(i18next.t('obtainingExplicitConsentMethod:messages.createSuccess', { defaultValue: 'Explicit consent method created' }));
            return { success: true, data: response.data };
        } catch (error) {
            const errorMessage = this._getErrorMessage(error);
            toast.error(errorMessage);
            return { success: false, error: errorMessage };
        } finally {
            store.setLoading(false);
        }
    }

    /**
     * Update explicit consent method
     */
    async updateExplicitConsentMethod(id, data) {
        const store = useExplicitConsentMethodStore.getState();
        const tenantId = useTenantStore.getState().selectedTenantId;

        store.setLoading(true);

        if (!tenantId) {
            store.setLoading(false);
            return { success: false, error: getIntegrationErrorMessage({ message: 'Tenant context required' }) };
        }

        try {
            const response = await this.service.update(id, { ...data, tenantId });
            toast.success(i18next.t('obtainingExplicitConsentMethod:messages.updateSuccess', { defaultValue: 'Explicit consent method updated' }));
            return { success: true, data: response.data };
        } catch (error) {
            const errorMessage = this._getErrorMessage(error);
            toast.error(errorMessage);
            return { success: false, error: errorMessage };
        } finally {
            store.setLoading(false);
        }
    }

    /**
     * Delete explicit consent method
     */
    async deleteExplicitConsentMethod(id) {
        const store = useExplicitConsentMethodStore.getState();
        const tenantId = useTenantStore.getState().selectedTenantId;

        store.setLoading(true);

        if (!tenantId) {
            store.setLoading(false);
            return { success: false, error: getIntegrationErrorMessage({ message: 'Tenant context required' }) };
        }

        try {
            const response = await this.service.delete(id);
            toast.success(i18next.t('obtainingExplicitConsentMethod:messages.deleteSuccess', { defaultValue: 'Explicit consent method deleted' }));
            return { success: true };
        } catch (error) {
            const errorMessage = this._getErrorMessage(error);
            toast.error(errorMessage);
            return { success: false, error: errorMessage };
        } finally {
            store.setLoading(false);
        }
    }

    /**
     * Bulk delete explicit consent methods
     */
    async bulkDeleteExplicitConsentMethods(ids) {
        const store = useExplicitConsentMethodStore.getState();
        const tenantId = useTenantStore.getState().selectedTenantId;

        store.setLoading(true);

        if (!tenantId) {
            store.setLoading(false);
            return { success: false, error: getIntegrationErrorMessage({ message: 'Tenant context required' }) };
        }

        try {
            // Use createBatchDelete if available in service, otherwise fallback logic could be here but service has it now
            const response = await this.service.createBatchDelete({ ids });
            toast.success(i18next.t('obtainingExplicitConsentMethod:messages.bulkDeleteSuccess', {
                defaultValue: 'Selected explicit consent methods deleted',
                count: ids.length,
            }));
            return { success: true, data: response.data };
        } catch (error) {
            const errorMessage = this._getErrorMessage(error);
            toast.error(errorMessage);
            return { success: false, error: errorMessage };
        } finally {
            store.setLoading(false);
        }
    }

    /**
     * Dropdown options for explicit consent forms (reference cache → seed → list API).
     */
    async fetchDropdownData() {
        const tenantId = useTenantStore.getState().selectedTenantId;

        try {
            return await fetchDropdownOptionsFromReference('explicitConsentMethod', async () => {
                if (!tenantId) {
                    return { success: false, data: [] };
                }
                const response = await this.service.getAll({
                    page: 1,
                    pageSize: 1000,
                    tenantId,
                });
                return { success: true, data: response.data || [] };
            });
        } catch (error) {
            return { success: false, error: this._getErrorMessage(error), data: [] };
        }
    }

    _getErrorMessage(error) {
        return getIntegrationErrorMessage(error, {
            notFoundDefault: 'Explicit consent method not found',
        });
    }
}
